// The shell: a 28-px top bar (wordmark, target, brain, take-over, user) over the target's screen with the chat on
// top; Admin and Settings replace the screen while open.
import { useCallback, useEffect, useState } from "react";
import { Admin } from "./Admin";
import { api, HttpError, type Me, type TargetT } from "./api";
import { Chat } from "./Chat";
import { Computers } from "./Computers";
import { useLive } from "./live";
import { Screen } from "./Screen";
import { Settings } from "./Settings";
import { Overview } from "./Overview";

type View = "screen" | "computers" | "admin" | "settings" | "overview";
const store = {
  get: (k: string) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k: string, v: string | null) => { try { v === null ? localStorage.removeItem(k) : localStorage.setItem(k, v); } catch { /* private mode */ } },
};

export function App() {
  const [me, setMe] = useState<Me | null | "out">(null);
  useEffect(() => {
    api.me().then(setMe).catch((e) => setMe(e instanceof HttpError && e.status === 401 ? "out" : null));
  }, []);
  if (me === "out") return <SignIn />;
  if (me === null) return null;
  return <Main me={me} refreshMe={() => api.me().then(setMe)} />;
}

// Why the last sign-in did not work, from the server's redirect (/?login_error=...): only these, so a crafted link
// cannot put words of its own on the page.
export const LOGIN_ERRORS: Record<string, (who: string) => string> = {
  failed: () => "Sign-in did not complete. Please try again.",
  not_invited: (who) => `${who || "This account"} has no access to Voitta KVM. Ask an administrator to add you, or use another account.`,
};

/** The sign-in card, in enterprise.voitta.ai's look: one centred card, Google's own button. */
export function SignIn() {
  const q = new URLSearchParams(window.location.search);
  const why = LOGIN_ERRORS[q.get("login_error") ?? ""]?.(q.get("email") ?? "");
  return (
    <div className="signin">
      <main className="signin-card" aria-labelledby="signin-title">
        <h1 id="signin-title">Sign in to Voitta KVM</h1>
        <div className="signin-body">
          {why && <p className="signin-err" role="alert">{why}</p>}
          <p className="signin-hint">Sign in with your Google account to continue.</p>
          <a className="gsi-button" href="/auth/login" role="button">
            <svg aria-hidden="true" viewBox="0 0 24 24" width="18" height="18">
              <path fill="var(--g-blue)" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="var(--g-green)" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="var(--g-yellow)" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="var(--g-red)" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            <span>Sign in with Google</span>
          </a>
        </div>
      </main>
    </div>
  );
}

function Main({ me, refreshMe }: { me: Me; refreshMe: () => void }) {
  const [targets, setTargets] = useState<TargetT[]>([]);
  const [tid, setTid] = useState<string | null>(store.get("vk.target"));
  const [view, setView] = useState<View>("screen");
  const [brain, setBrain] = useState(store.get("vk.brain") || "claude");
  const [menu, setMenu] = useState<"" | "target" | "brain" | "user">("");
  const live = useLive(view === "screen" ? tid : null);
  const [dock, setDock] = useState(0);
  const onDock = useCallback((px: number) => setDock(px), []);

  const loadTargets = useCallback(() => api.targets().then((ts) => {
    setTargets(ts);
    setTid((cur) => (ts.some((t) => t.id === cur) ? cur : ts[0]?.id ?? null));
  }).catch(() => undefined), []);
  useEffect(() => { loadTargets(); }, [loadTargets]);
  useEffect(() => { store.set("vk.target", tid); }, [tid]);
  useEffect(() => { store.set("vk.brain", brain); }, [brain]);

  const target = targets.find((t) => t.id === tid);
  const human = live.state === "human";
  const canDrive = live.state !== "busy" && live.state !== "connecting" && live.state !== "closed";
  const canChat = brain === "claude" && !me.claude.connected ? "Connect your Claude subscription in Settings to chat."
    : brain === "codex" && !me.codex.connected ? "Connect your ChatGPT account in Settings to use Codex." : null;
  const dot = { idle: "ok", running: "run", preparing: "run", stopping: "run", human: "run", busy: "warn", error: "err", closed: "err", connecting: "" }[live.state] ?? "";

  // Release: the server stops a running task, ends a take-over and frees the computer; this tab then shows the
  // computers to connect to. Not connected (already free): just go there.
  const release = () => {
    const done = () => { setView("computers"); loadTargets(); };
    if (!live.send({ t: "release" })) { done(); return; }
    window.setTimeout(done, live.state === "idle" ? 300 : 1500);
  };
  const connect = (id: string) => { setTid(id); setView("screen"); setMenu(""); };

  const setTheme = (t: string | null) => {
    if (t) document.documentElement.dataset.theme = t; else delete document.documentElement.dataset.theme;
    store.set("vk.theme", t);
  };

  return (
    <div className="app" onClick={() => menu && setMenu("")}>
      <header className="topbar" onClick={(e) => e.stopPropagation()}>
        <span className="wordmark" onClick={() => setView("screen")} style={{ cursor: "pointer" }}>voitta <b>kvm</b></span>
        <span className="sep" />
        <div className="menu" style={{ minWidth: 0 }}>
          <button className="picker" onClick={() => setMenu(menu === "target" ? "" : "target")}>
            <span className={`dot ${view === "screen" ? dot : ""}`} />
            <span className="name">{target?.name ?? "No computer"}</span>
            {target && <span className="muted hide-narrow">{target.os} · {target.path ?? target.kind}</span>}
            <span className="caret">▼</span>
          </button>
          {menu === "target" && (
            <div className="menu-list">
              {targets.length === 0 && <div className="menu-item" aria-disabled="true">No computers yet</div>}
              {targets.map((t) => (
                <button key={t.id} className="menu-item" onClick={() => { setTid(t.id); setView("screen"); setMenu(""); }}>
                  <span className={`dot ${t.holder && t.holder !== me.email ? "warn" : t.id === tid ? "ok" : ""}`} />
                  <span className="grow">{t.name}</span>
                  <span className="muted">{t.holder && t.holder !== me.email ? `${t.holder.split("@")[0]}${t.via === "mcp" ? " (MCP)" : ""}`
                    : t.cable ? "not on the cable" : t.kvm_busy ? `${t.path} in use` : `${t.os} · ${t.path ?? t.kind}`}</span>
                </button>
              ))}
              <div className="menu-rule" />
              <button className="menu-item" onClick={() => { setView("computers"); setMenu(""); }}>All computers and KVMs…</button>
            </div>
          )}
        </div>
        <span className="grow" />
        {view === "screen" && live.state === "busy" &&
          <span className="note hide-narrow">{live.what ? `${live.what}${live.holder ? `, in use by ${live.holder}` : ""}`
            : `In use by ${live.holder}${live.via === "mcp" ? " (MCP)" : ""}`}</span>}
        <div className="menu">
          <button className="picker" onClick={() => setMenu(menu === "brain" ? "" : "brain")}>
            <span className="name">{brain === "codex" ? "Codex" : "Claude"}</span><span className="caret">▼</span>
          </button>
          {menu === "brain" && (
            <div className="menu-list right" style={{ minWidth: 200 }}>
              <button className="menu-item" onClick={() => { setBrain("claude"); setMenu(""); }}>
                <span className={`dot ${me.claude.connected ? "ok" : ""}`} /><span className="grow">Claude</span>
                <span className="muted">{me.claude.connected ? "subscription" : "not connected"}</span>
              </button>
              <button className="menu-item" onClick={() => { setBrain("codex"); setMenu(""); }}>
                <span className={`dot ${me.codex.connected ? "ok" : ""}`} /><span className="grow">Codex</span>
                <span className="muted">{me.codex.connected ? "ChatGPT" : "not connected"}</span>
              </button>
            </div>
          )}
        </div>
        {view === "screen" && tid && (
          <button className="btn quiet" title="Give this computer up (stops a running task) and choose another"
            disabled={live.state === "connecting" || live.state === "busy"} onClick={release}>
            Release
          </button>
        )}
        {view === "screen" && (
          <button className={`btn ${human ? "primary" : ""}`} disabled={!canDrive}
            onClick={() => live.send({ t: human ? "handback" : "takeover" })}>
            {human ? "Hand back" : "Take over"}
          </button>
        )}
        {me.viewed_by && (
          <span className="viewing-as" role="status" title={`${me.viewed_by} is viewing as ${me.email}`}>
            Viewing as <b>{me.email}</b>
            <button className="btn quiet" onClick={() => api.stopViewAs().then(() => location.assign("/"))}>Stop</button>
          </span>
        )}
        <span className="sep" />
        <div className="menu">
          <div className="avatar" title={me.email} onClick={() => setMenu(menu === "user" ? "" : "user")}>
            {me.email.slice(0, 2).toUpperCase()}
          </div>
          {menu === "user" && (
            <div className="menu-list right">
              <div className="menu-item" aria-disabled="true">{me.email}</div>
              <div className="menu-rule" />
              <button className="menu-item" onClick={() => { setView("screen"); setMenu(""); }}>Screen</button>
              <button className="menu-item" onClick={() => { setView("computers"); setMenu(""); }}>Computers</button>
              <button className="menu-item" onClick={() => { setView("settings"); setMenu(""); }}>Settings</button>
              {me.role === "admin" && <button className="menu-item" onClick={() => { setView("admin"); setMenu(""); }}>Admin</button>}
              {me.superadmin === true && <button className="menu-item" onClick={() => { setView("overview"); setMenu(""); }}>Account overview</button>}
              <div className="menu-rule" />
              <div className="menu-item" aria-disabled="true"><span className="label">Theme</span></div>
              {([["System", null], ["Light", "light"], ["Dark", "dark"]] as const).map(([n, v]) => (
                <button key={n} className="menu-item" onClick={() => { setTheme(v); setMenu(""); }}>
                  <span className="grow">{n}</span>{(store.get("vk.theme") ?? null) === v && <span>✓</span>}
                </button>
              ))}
              <div className="menu-rule" />
              <button className="menu-item" onClick={() => api.logout().then(() => location.reload())}>Sign out</button>
            </div>
          )}
        </div>
      </header>

      {view === "screen" && (
        <main className="stage">
          {target ? (
            <>
              <div className={`screen-area ${dock > 28 ? "docked" : ""}`} style={{ right: dock }}><Screen live={live} /></div>
              <Chat live={live} targetId={target.id} brain={brain} canChat={canChat} onDock={onDock} />
            </>
          ) : (
            <div className="empty"><div><h2>No computer</h2><div>Add yours in <a href="#" onClick={(e) => { e.preventDefault(); setView("computers"); }}>Computers</a>, or ask someone to share one.</div></div></div>
          )}
        </main>
      )}
      {view === "computers" && <Computers me={me} current={tid} onConnect={connect} onRefresh={loadTargets} />}
      {view === "admin" && <Admin me={me} />}
      {view === "settings" && <Settings me={me} onChange={refreshMe} />}
      {view === "overview" && me.superadmin === true && <Overview me={me} />}
    </div>
  );
}
